'use client';

import { useParams, useRouter } from 'next/navigation';

import { memo, useEffect } from 'react';

import DOMPurify from 'isomorphic-dompurify';

import ImageProxy from '@next-image-proxy';

import { Spinner } from '@uikit';

import { useHotToGetOfferQuery } from '@query';

import PageHeader from '@store:web/components/layout/PageHeader/PageHeader';
import HotOfferCard from '@store:web/components/pages/HotOfferPage/HotOfferCard/HotOfferCard';

interface HotOfferPageProps {
  status?: string;
}

type HotOfferPageParams = {
  offerId: string;
};

function HotOfferPage({ status }: HotOfferPageProps) {
  const router = useRouter();
  const { offerId } = useParams<HotOfferPageParams>();

  const { data, isPending: isLoading } = useHotToGetOfferQuery(offerId, {
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (!isLoading && !data) {
      router.push('/404');
    }
  }, [isLoading, data, router]);

  const bannerAlt = `${data?.attributes.name} Magic Store Hot Offer 1`;

  if (!data) {
    return <Spinner size="md" className="m-auto" />;
  }

  return (
    <>
      <PageHeader
        disableBanner
        page="hot-offers"
        extendBreadcrumbs={[
          {
            label: data?.attributes.name,
            pathname: `/hot-offers/${data?.id}`,
          },
        ]}
      />

      <main className="v2-container flex flex-col gap-4 md:gap-8">
        <article className="w-full flex flex-col items-center pb-2 rounded-2xl overflow-hidden">
          <ImageProxy
            className="w-full object-cover bg-primary-100 sm:hidden"
            src={data?.attributes.banner}
            alt={bannerAlt}
            width={800}
            height={300}
            sizes="100vw"
          />

          <ImageProxy
            className="w-full object-cover bg-primary-100 max-sm:hidden"
            src={data?.attributes.pageBackImage}
            alt={bannerAlt}
            width={1630}
            height={280}
            sizes="100vw"
            data-qa="hot-offer-banner-image"
          />

          <div className="flex flex-col gap-6 w-full max-w-[1200px] p-3 pb-0 md:flex-row md:gap-8 lg:p-6 lg:pb-0 lg:gap-11 3xl:gap-20">
            <HotOfferCard status={status} offer={data} />

            <div className="w-full flex flex-col px-6 md:px-0 max-lg:px-0">
              <p
                className="text-neutral-600 leading-[1.7rem] prose-a:text-primary-700 prose-a:underline"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data?.attributes.howToGet) }}
              />
              {data?.attributes.cashbackDescription && (
                <p
                  className="text-neutral-600 leading-[1.7rem] mt-4"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(data?.attributes.cashbackDescription),
                  }}
                />
              )}
            </div>
          </div>
        </article>
      </main>
    </>
  );
}

export default memo(HotOfferPage);
